import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import BootSplash from "@/components/BootSplash";
import { I18nProvider } from "@/i18n";
import PartnerLoginPage from "@/site/pages/PartnerLoginPage";
import ImpersonationBar from "@/components/ImpersonationBar";
import UrlMask, { Unmask } from "@/components/UrlMask";
import { routerWindowFor } from "@/lib/urlMask";
import SiteLayout from "@/site/SiteLayout";
import LandingPage from "@/site/pages/LandingPage";
/* the website builder (2026-09-12): published CMS pages render on the site's catch-all, the
   builder itself is a superadmin app — both stay out of every ordinary visit's bundle */
const CmsPageRoute = lazy(() => import("@/site/cms/render/CmsPageRoute"));
const SiteBuilder = lazy(() => import("@/site/cms/builder/Builder"));
/* the rest of the public site loads on demand (each page is its own chunk) */
const CollectionsPage = lazy(() => import("@/site/pages/CollectionsPage"));
const ProductDetailPage = lazy(() => import("@/site/pages/ProductDetailPage"));
const MaterialsPage = lazy(() => import("@/site/pages/MaterialsPage"));
const MaterialDetailPage = lazy(() => import("@/site/pages/MaterialDetailPage"));
const ProjectsPage = lazy(() => import("@/site/pages/ProjectsPage"));
const ProjectDetailPage = lazy(() => import("@/site/pages/ProjectDetailPage"));
const ContractPage = lazy(() => import("@/site/pages/ContractPage"));
const AboutPage = lazy(() => import("@/site/pages/AboutPage"));
const ContactPage = lazy(() => import("@/site/pages/ContactPage"));
const ResourcesPage = lazy(() => import("@/site/pages/ResourcesPage"));
const LegalPage = lazy(() => import("@/site/pages/LegalPage"));
/* the two data-driven templates: 25 category listings + world hubs, and all 89 piece pages */
const CatalogPage = lazy(() => import("@/site/pages/CatalogPage"));
const PieceDetailPage = lazy(() => import("@/site/pages/PieceDetailPage"));
/* the prototype's kitchen fallback: lines without their own page render kitchen-detail.html */
const KitchenDetailPage = lazy(() => import("@/site/pages/KitchenDetailPage"));
import AdminApp from "@/admin/AdminApp";
import CatalogTechApp from "@/catalog/CatalogTechApp";
import StoreApp from "@/store/StoreApp";
import ManufacturerApp from "@/manufacturer/ManufacturerApp";
import InstallerApp from "@/installer/InstallerApp";
import ClientPortal from "@/portal/ClientPortal";
import { studioUsesReact } from "@/studio/studioEngineFlag";
// Platform-wide premium polish layer (2026-07-18) — loads LAST among global
// styles so its additive refinements (numerals/motion/focus/scrollbars/toasts)
// win the cascade without touching any design system's layout rules.
import "@/styles/premium.css";
import "@/styles/typography.css";   // THE type system (2026-09-01) — loads last, wins the cascade
/* React Router routes on the REAL url behind the masked bar (lib/urlMask.js) */
const ROUTER_WINDOW = typeof window !== "undefined" ? routerWindowFor(window) : undefined;

// The configurator carries the heavy CAD markup + engine bridge; load it only on
// the /studio routes so it never weighs down the rest of the app.
const ConfiguratorApp = lazy(() => import("@/studio/configurator/ConfiguratorApp"));
const ConfiguratorFrame = lazy(() => import("@/studio/ConfiguratorFrame"));
const StudioConfigurator = lazy(() => import("@/studio/StudioConfigurator"));
const CadApp = lazy(() => import("@/studio/cad/CadApp"));
const CadProApp = lazy(() => import("@/cad/CadProApp"));   // new mm-based CAD-grade workspace (Milestone 1)
// Migration: dev-only parity harnesses for the new pure React engine (NOT production).
const ConfiguratorReactDev = lazy(() => import("@/configurator/components/ConfiguratorReact"));
const LiveParityHarness = lazy(() => import("@/configurator/components/LiveParityHarness"));
// React-mode studio shell: the parity-certified editor + the ported screens
// (PreviewPage) as sub-routes over the always-mounted editor.
const StudioReactShell = lazy(() => import("@/studio/StudioReactShell"));
// /studio edit engine selector: the React shell (default) vs the original iframe.
// Flip per session via ?react=0 / ?react=1 (sticky — studioEngineFlag.js).
function StudioRoute() {
  return studioUsesReact() ? <StudioReactShell /> : <ConfiguratorFrame />;
}
// Account redesigns ported from sales.html (pixel-perfect). Preview routes until
// they reach parity + wiring, then they replace the existing role apps.
const SuperAdminApp = lazy(() => import("@/admin/superadmin/SuperAdminApp"));
const SalesCrmApp = lazy(() => import("@/salescrm/SalesCrmApp"));
// Store-scoped Timeline (Stage C port of the prototype's Gantt) — cross-project
// data page, no engine; the studio iframe's setAppView('timeline') retargets here.
const TimelinePage = lazy(() => import("@/store/TimelinePage"));

export const ADMIN_ROLES = ["SUPERADMIN", "ADMIN", "CATALOG_MANAGER"];
export const STORE_ROLES = ["DEALER_OWNER", "STORE_ADMIN", "SALES", "DESIGNER", "STORE"];
export const MANUFACTURER_ROLES = ["MANUFACTURER_ADMIN", "PRODUCTION_MANAGER", "FINANCE_MANAGER", "LOGISTICS_MANAGER"];
export const INSTALLER_ROLES = ["INSTALLER"];
export const CLIENT_ROLES = ["CLIENT"];

export const CATALOG_ROLES = ["CATALOG_MANAGER", "SUPERADMIN", "ADMIN"];

const FALLBACK_HOME = {
  SUPERADMIN: "/admin", ADMIN: "/admin", CATALOG_MANAGER: "/catalog",
  DEALER_OWNER: "/store", STORE_ADMIN: "/store", SALES: "/sales", DESIGNER: "/store", STORE: "/store",
  MANUFACTURER_ADMIN: "/manufacturer", PRODUCTION_MANAGER: "/manufacturer",
  FINANCE_MANAGER: "/manufacturer", LOGISTICS_MANAGER: "/manufacturer",
  INSTALLER: "/installer", CLIENT: "/client-portal",
};

export function homePathForUser(user) {
  if (!user) return "/login";
  return user.default_route || FALLBACK_HOME[user.role] || "/login";
}

function Loading() {
  return (
    <div data-testid="auth-loading" style={fullCenter}>
      <div style={{ textAlign: "center" }}>
        <div style={spinner} />
        <div style={{ marginTop: 14, fontSize: 13, color: "#71717a" }}>Checking your session…</div>
      </div>
    </div>
  );
}

function FeatureDisabled({ feature }) {
  const { user } = useAuth();
  return (
    <div data-testid="feature-disabled" style={fullCenter}>
      <div style={{ textAlign: "center", maxWidth: 420 }}>
        <div style={{ fontSize: 13, letterSpacing: ".14em", textTransform: "uppercase", color: "#a1a1aa" }}>Unavailable</div>
        <h1 style={{ fontSize: 24, margin: "8px 0 10px", color: "#18181b" }}>This feature is turned off</h1>
        <p style={{ color: "#52525b", fontSize: 14, lineHeight: 1.6 }}>
          The <strong>{feature}</strong> feature has been disabled for your account by the platform administrator.
        </p>
        <Link to={homePathForUser(user)} style={{ ...btnDark, display: "inline-block", marginTop: 18 }}>Back to my workspace</Link>
      </div>
    </div>
  );
}

function ProtectedRoute({ children, allow, feature }) {
  const { status, user } = useAuth();
  if (status === "loading") return <Loading />;
  if (status !== "authenticated") return <Navigate to="/partners/login" replace />;
  if (allow && !allow.includes(user?.role)) return <Navigate to="/forbidden" replace />;
  // Feature-flag gate: a disabled feature is stripped from allowed_features in auth/me.
  if (feature && Array.isArray(user?.allowed_features) && !user.allowed_features.includes(feature)) {
    return <FeatureDisabled feature={feature} />;
  }
  return children;
}

function Forbidden() {
  const { user, logout } = useAuth();
  return (
    <div data-testid="forbidden-page" style={fullCenter}>
      <div style={{ textAlign: "center", maxWidth: 420 }}>
        <div style={{ fontSize: 13, letterSpacing: ".14em", textTransform: "uppercase", color: "#a1a1aa" }}>403</div>
        <h1 style={{ fontSize: 26, margin: "8px 0 10px", color: "#18181b" }}>Access not allowed</h1>
        <p style={{ color: "#52525b", fontSize: 14, lineHeight: 1.6 }}>
          Your role ({user?.role || "unknown"}) doesn't have access to this area.
        </p>
        <div style={{ marginTop: 18, display: "flex", gap: 10, justifyContent: "center" }}>
          <Link to={homePathForUser(user)} style={btnDark} data-testid="forbidden-home">Go to my workspace</Link>
          <button onClick={logout} style={btnGhost} data-testid="forbidden-logout">Sign out</button>
        </div>
      </div>
    </div>
  );
}

function RoleLanding({ title, blurb, testid }) {
  const { user, logout } = useAuth();
  const b = user?.branding_context || {};
  return (
    <div data-testid={testid} style={{ ...fullCenter, background: "#0f1012", color: "#e7e7ea" }}>
      <div style={{ textAlign: "center", maxWidth: 480, padding: 24 }}>
        <div style={{ fontSize: 12, letterSpacing: ".16em", textTransform: "uppercase", color: "#8a8a93" }}>
          {b.company_name || "LHC Platform"}
        </div>
        <h1 style={{ fontSize: 28, margin: "10px 0 12px" }}>{title}</h1>
        <p style={{ color: "#a1a1aa", fontSize: 14.5, lineHeight: 1.6 }}>{blurb}</p>
        <div style={{ marginTop: 16, fontSize: 13, color: "#c4c4cc" }}>
          Signed in as <strong>{user?.name}</strong> · {user?.email} · {user?.role}
        </div>
        <button onClick={logout} style={{ ...btnGhost, marginTop: 22, color: "#e7e7ea", borderColor: "rgba(255,255,255,.2)" }} data-testid={`${testid}-logout`}>
          Sign out
        </button>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter window={ROUTER_WINDOW}>
      {/* 2 s brand animation on EVERY page load (2026-08-26 user: "for
          animation is always shown on refresh page always"). Outside Routes so
          a route change never replays it, and pointer-events:none so the app
          under it is usable from the first frame. */}
      <BootSplash />
      {/* ONE translation engine for the whole platform (V5): language +
          direction + locale-aware formatting. Wraps everything so any surface
          can translate without its own setup. */}
      <I18nProvider>
      <AuthProvider>
        {/* superadmin "log in as this dealer" sessions carry a return-to-admin pill */}
        <ImpersonationBar />
        {/* short address-bar URLs (/store/client, /store/project, /studio …) — routing stays on the real path */}
        <UrlMask />
        <Suspense fallback={<Loading />}>
        <Routes>
          {/* THE PUBLIC SITE (2026-09-07: lhc-site/*.html ported) — the platform's main site lives at "/" */}
          <Route element={<SiteLayout />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/partners/login" element={<PartnerLoginPage />} />
            <Route path="/partners/apply" element={<PartnerLoginPage />} />
            <Route path="/collections" element={<CollectionsPage />} />
            <Route path="/materials" element={<MaterialsPage />} />
            <Route path="/materials/:slug" element={<MaterialDetailPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/:slug" element={<ProjectDetailPage />} />
            <Route path="/contract" element={<ContractPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/legal" element={<LegalPage />} />
            {/* The product worlds. Every one of the prototype's 129 pages resolves here:
                a world hub, a category listing (/indoor/sofas), or a piece (/indoor/sofas/ernest).
                site/prototypeRoutes.js is the generated file→route map SiteLink resolves against. */}
            <Route path="/products" element={<CatalogPage world="products" />} />
            <Route path="/products/:category/:slug" element={<ProductDetailPage />} />
            <Route path="/kitchens" element={<CatalogPage world="kitchens" />} />
            <Route path="/kitchens/:slug" element={<PieceDetailPage fallback={<KitchenDetailPage />} />} />
            <Route path="/systems" element={<CatalogPage world="systems" />} />
            <Route path="/systems/:category" element={<CatalogPage world="systems" />} />
            <Route path="/systems/:category/:slug" element={<PieceDetailPage />} />
            <Route path="/indoor" element={<CatalogPage world="indoor" />} />
            <Route path="/indoor/:category" element={<CatalogPage world="indoor" />} />
            <Route path="/indoor/:category/:slug" element={<PieceDetailPage />} />
            <Route path="/outdoor" element={<CatalogPage world="outdoor" />} />
            <Route path="/outdoor/:category" element={<CatalogPage world="outdoor" />} />
            <Route path="/outdoor/:category/:slug" element={<PieceDetailPage />} />
            {/* anything unknown is a website-builder page (published CMS slug) or the site's own 404
                inside the shell — CmsPageRoute renders SiteNotFound itself when no page matches */}
            <Route path="*" element={<CmsPageRoute />} />
          </Route>
          {/* the old login/signup screens are retired — the site's Partner Account page is THE sign-in */}
          <Route path="/login" element={<Navigate to="/partners/login" replace />} />
          <Route path="/signup" element={<Navigate to="/partners/apply" replace />} />
          <Route path="/forbidden" element={<Forbidden />} />
          {/* Super Admin / Developer dashboard (sales.html port) is now the primary
              /admin surface — pixel-perfect, with Users/Dealers/Flags/Audit on live data. */}
          {/* the website builder (static segments outrank the /admin/* splat) — superadmin only,
              matching the backend's require_superadmin on every /admin/site/* endpoint */}
          <Route path="/admin/website/builder/*" element={<ProtectedRoute allow={["SUPERADMIN"]}><Suspense fallback={null}><SiteBuilder /></Suspense></ProtectedRoute>} />
          <Route path="/admin/*" element={<ProtectedRoute allow={ADMIN_ROLES}><SuperAdminApp /></ProtectedRoute>} />
          <Route path="/superadmin/*" element={<ProtectedRoute allow={ADMIN_ROLES}><SuperAdminApp /></ProtectedRoute>} />
          {/* Legacy admin kept reachable as a fallback during the transition. */}
          <Route path="/admin-legacy/*" element={<ProtectedRoute allow={ADMIN_ROLES}><AdminApp /></ProtectedRoute>} />
          <Route path="/catalog/*" element={<ProtectedRoute allow={CATALOG_ROLES}><CatalogTechApp /></ProtectedRoute>} />
          {/* Real React store workspace is now the primary /store surface (strangler). */}
          {/* Timeline: static segment outranks the /store/* splat, so this wins. */}
          {/* display paths the browser lands on after a reload / back-forward: restore the real record */}
          <Route path="/store/client" element={<Unmask fallback="/store/clients" />} />
          <Route path="/store/project" element={<Unmask fallback="/store/clients" />} />
          <Route path="/store/report" element={<Unmask fallback="/store/clients" />} />
          <Route path="/admin/application" element={<Unmask fallback="/admin" />} />
          <Route path="/sales/order" element={<Unmask fallback="/sales" />} />
          <Route path="/studio" element={<Unmask><ProtectedRoute feature="configurator_3d"><StudioRoute /></ProtectedRoute></Unmask>} />
          <Route path="/store/timeline" element={<ProtectedRoute allow={STORE_ROLES}><TimelinePage /></ProtectedRoute>} />
          <Route path="/store/*" element={<ProtectedRoute allow={STORE_ROLES}><StoreApp /></ProtectedRoute>} />
          {/* Platform Sales CRM (mandate 2026-07-05) — the single global sales team */}
          <Route path="/sales/*" element={<ProtectedRoute allow={["SALES", "SUPERADMIN", "ADMIN"]}><SalesCrmApp /></ProtectedRoute>} />
          {/* Legacy prototype workspace retired from the UI — anything pointing here goes to the React store. */}
          <Route path="/dealer/*" element={<Navigate to="/store" replace />} />
          <Route path="/manufacturer/*" element={<ProtectedRoute allow={MANUFACTURER_ROLES} feature="production_pipeline"><ManufacturerApp /></ProtectedRoute>} />
          <Route path="/installer/*" element={<ProtectedRoute allow={INSTALLER_ROLES} feature="installer"><InstallerApp /></ProtectedRoute>} />
          <Route path="/client-portal/*" element={<ProtectedRoute allow={CLIENT_ROLES}><ClientPortal /></ProtectedRoute>} />
          {/* Configurator + wizard: the UNTOUCHED original engine in its own isolated
              document (own CSS / globals / event system, fully laid out) → guaranteed
              byte-identical behavior with the original. Bridged to the backend for
              catalog, design restore/save, and navigation. The React app wraps it. */}
          {/* Stage C: trailing /* lets the StudioShell (ConfiguratorFrame) render its
              internal sub-routes (models/wizard/preview) over the always-alive iframe. */}
          <Route path="/studio/:projectId?/*" element={<ProtectedRoute feature="configurator_3d"><StudioRoute /></ProtectedRoute>} />
          {/* Parallel: in-page React-hosted JSX markup driving the same engine (kept for
              reference; the in-page host has integration hazards vs. the isolated frame). */}
          <Route path="/studio-jsx/:projectId?" element={<ProtectedRoute feature="configurator_3d"><ConfiguratorApp /></ProtectedRoute>} />
          {/* Legacy: innerHTML-hosted configurator (kept as fallback). */}
          <Route path="/studio-react/:projectId?" element={<ProtectedRoute feature="configurator_3d"><StudioConfigurator /></ProtectedRoute>} />
          {/* Native React + R3F CAD rewrite (no engine). Phase 1: renders saved designs. */}
          <Route path="/studio-cad/:projectId?" element={<ProtectedRoute feature="configurator_3d"><CadApp /></ProtectedRoute>} />
          {/* HIDDEN DEV/DEBUG ONLY: standalone harness to develop the reusable CAD renderers in
              isolation. NOT the product workspace (that's /studio, the untouched iframe) and not
              linked anywhere in the product nav. The renderers also mount inside /studio behind
              REACT_APP_STUDIO_CAD_RENDERERS. */}
          <Route path="/cad/:projectId?" element={<ProtectedRoute feature="configurator_3d"><CadProApp /></ProtectedRoute>} />
          {/* HIDDEN DEV ONLY (Step 4): the real React configurator UI (pixel-perfect to the
              prototype, wired to the extracted engine) + visual comparison mode. /studio stays iframe. */}
          <Route path="/studio-react-dev/:projectId?" element={<ProtectedRoute feature="configurator_3d"><ConfiguratorReactDev /></ProtectedRoute>} />
          {/* Live iframe-vs-extracted-engine DATA parity certification (Step 3). */}
          <Route path="/studio-react-dev-parity/:projectId?" element={<ProtectedRoute feature="configurator_3d"><LiveParityHarness /></ProtectedRoute>} />
        </Routes>
        </Suspense>
      </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

const fullCenter = {
  position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
  background: "#fff", fontFamily: "Inter, -apple-system, sans-serif",
};
const spinner = {
  width: 32, height: 32, borderRadius: "50%", margin: "0 auto",
  border: "3px solid #e4e4e7", borderTopColor: "#18181b", animation: "lhcspin .8s linear infinite",
};
const btnDark = { padding: "10px 16px", borderRadius: 10, background: "#18181b", color: "#fff", textDecoration: "none", fontSize: 13, fontWeight: 600 };
const btnGhost = { padding: "10px 16px", borderRadius: 10, background: "transparent", color: "#18181b", border: "1px solid rgba(0,0,0,.15)", fontSize: 13, fontWeight: 600, cursor: "pointer" };

if (typeof document !== "undefined" && !document.getElementById("lhc-spin-kf")) {
  const s = document.createElement("style");
  s.id = "lhc-spin-kf";
  s.textContent = "@keyframes lhcspin{to{transform:rotate(360deg)}}";
  document.head.appendChild(s);
}
